import type { Step } from 'react-joyride';

import type { FertWorkTab } from '../components/FertWorkSession/TabBar';

export interface UnifiedTourStep extends Step {
  data?: { tab?: FertWorkTab };
}

export const unifiedSessionSteps: UnifiedTourStep[] = [
  {
    target: '.fert-work-session-page',
    title: 'Session Detail',
    content:
      'Welcome to the session detail view. This walkthrough will guide you through each tab: Session, Runs, Labels, Maps, and SOF Labels.',
    placement: 'center',
    disableBeacon: true,
    data: { tab: 'session' },
  },
  {
    target: '[data-tour="session-header"]',
    title: 'Session Header',
    content:
      'Shows the session location, date, and a refresh button to re-pull data from the source system.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'session' },
  },
  {
    target: '[data-tour="session-tabs"]',
    title: 'Navigation Tabs',
    content:
      'Switch between Session, Runs, Labels, Maps, and SOF Labels. Each tab shows a colored badge with the count of generated items. Green means all items are valid. Yellow means some have warnings. Red means there are blocking issues that must be fixed before printing.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'session' },
  },
  {
    target: '[data-tour="alert-pills"]',
    title: 'Alerts & Issues',
    content:
      'Quick counts of blocking issues, warnings, and available revisions. Click a pill to expand details.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'session' },
  },

  {
    target: '.generate-runs-page',
    title: 'Fertilization Runs',
    content:
      'The Runs tab groups donors by sire and mat-time window into fertilization runs.',
    placement: 'center',
    disableBeacon: true,
    data: { tab: 'runs' },
  },
  {
    target: '[data-tour="runs-actions"]',
    title: 'Run Actions',
    content:
      'Regenerate runs from scratch, print a summary sheet, or add an empty run to manually assign donors.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'runs' },
  },
  {
    target: '[data-tour="runs-grid"]',
    title: 'Run Cards',
    content:
      'Each card represents a fertilization run grouped by sire. Cards show the sire name, semen type, donor list, oocyte counts, and mat-time window.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'runs' },
  },
  {
    target: '[data-tour="runs-unassigned"]',
    title: 'Unassigned Donors',
    content:
      'Donors that could not be auto-assigned appear here in an orange box. Drag them into a run below to assign them.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'runs' },
  },
  {
    target: '[data-tour="runs-donor-handle"]',
    title: 'Drag to Reassign',
    content:
      'Click and hold a donor chip, then drag it to a different run card to reassign them. The target card highlights blue when you hover over it.',
    placement: 'right',
    disableBeacon: true,
    data: { tab: 'runs' },
  },
  {
    target: '[data-tour="runs-drop-target"]',
    title: 'Drop Target',
    content:
      'Drop a donor onto any run card. The card highlights blue when you hover over it with a donor. Release to complete the move.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'runs' },
  },

  {
    target: '.generate-labels-page',
    title: 'IVF/Flush Plate Labels',
    content:
      'The Labels tab shows plate labels generated from your runs. Each label pairs donor matings for IVF or flush plates.',
    placement: 'center',
    disableBeacon: true,
    data: { tab: 'labels' },
  },
  {
    target: '[data-tour="labels-actions"]',
    title: 'Label Actions',
    content:
      'Add a blank plate, regenerate all labels from the current runs, or print the full set as a PDF.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'labels' },
  },
  {
    target: '[data-tour="labels-color-key"]',
    title: 'Color Key',
    content:
      'Labels are color-coded by aspiration type: purple for Day 5, yellow for Export, blue for Freeze Only, white for Ship, and a red stripe for half-plate or different-sire labels.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'labels' },
  },
  {
    target: '[data-tour="labels-grid"]',
    title: 'Label Cards',
    content:
      'Each card represents a plate label. The colored stripe indicates plan category, and the card shows top/bottom donor matings with breed, sire, semen type, and oocyte count.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'labels' },
  },
  {
    target: '[data-tour="labels-card-handle"]',
    title: 'Label Card Grab Handle',
    content:
      'The dotted grip strip on the left edge of each label card lets you drag the entire card to reorder it in the sequence. Grab it, drag up or down, and drop into a new position.',
    placement: 'right',
    disableBeacon: true,
    data: { tab: 'labels' },
  },
  {
    target: '[data-tour="labels-mating-handle"]',
    title: 'Mating Grab Handle',
    content:
      'Inside each label, individual matings (top/bottom) also have a small dotted grip on their left side. Drag a mating from one label and drop it onto an empty slot in another label to move it.',
    placement: 'right',
    disableBeacon: true,
    data: { tab: 'labels' },
  },

  {
    target: '.generate-incubator-maps-page',
    title: 'Incubator Maps',
    content:
      'The Maps tab manages plate placement across your HeraCell and Astec incubators.',
    placement: 'center',
    disableBeacon: true,
    data: { tab: 'maps' },
  },
  {
    target: '[data-tour="maps-actions"]',
    title: 'Map Actions',
    content:
      'Regenerate the map layout, print it, or open Settings to change incubator counts and fill preference.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'maps' },
  },
  {
    target: '[data-tour="maps-rules"]',
    title: 'Placement Rules',
    content:
      'Expand this panel to see automatic placement rules. Day 5 and Export plates are always isolated on their own incubator. Standard plates (Freeze Only, Ship) share space together.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'maps' },
  },
  {
    target: '[data-tour="maps-stats"]',
    title: 'Slot Statistics',
    content:
      'A quick overview of total, occupied, and available incubator slots across all your incubators.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'maps' },
  },
  {
    target: '[data-tour="maps-incubators"]',
    title: 'Incubator Grid',
    content:
      'Each incubator is displayed as a grid of slots. HeraCell incubators use a 5x5 layout (A-E rows, 1-5 columns). Astec incubators have 10 chambers with 2 slots each.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'maps' },
  },
  {
    target: '[data-tour="maps-plate-handle"]',
    title: 'Plate Grab Handle',
    content:
      'Each occupied slot has a dotted grip on its left edge. Click and hold the grip to pick up the plate, then drag it to any empty slot in the same or a different incubator.',
    placement: 'right',
    disableBeacon: true,
    data: { tab: 'maps' },
  },

  {
    target: '.generate-sof-labels-page',
    title: 'IVC Plate Labels',
    content:
      'The IVC tab shows plate labels grouped by incubator, generated from your incubator map layout. Each label gets a unique IVC identifier (e.g. IVC-A1).',
    placement: 'center',
    disableBeacon: true,
    data: { tab: 'sof-labels' },
  },
  {
    target: '[data-tour="sof-actions"]',
    title: 'Print IVC Labels',
    content: 'Print all IVC labels across every incubator as a single PDF.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'sof-labels' },
  },
  {
    target: '[data-tour="sof-color-key"]',
    title: 'IVC Color Key',
    content:
      'Labels are color-coded the same way as the Labels tab: purple for Day 5, yellow for Export, blue for Freeze Only, and a red stripe for half-plate or different-sire labels.',
    placement: 'bottom',
    disableBeacon: true,
    data: { tab: 'sof-labels' },
  },
  {
    target: '[data-tour="sof-incubator-group"]',
    title: 'Incubator Group',
    content:
      'Labels are grouped by incubator with a letter identifier (A, B, C...). Each group shows its incubator name and label count.',
    placement: 'top',
    disableBeacon: true,
    data: { tab: 'sof-labels' },
  },
  {
    target: '[data-tour="sof-print-group"]',
    title: 'Print Single Incubator',
    content: 'Print IVC labels for just this incubator instead of all at once.',
    placement: 'left',
    disableBeacon: true,
    data: { tab: 'sof-labels' },
  },
];
